import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Save, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setCfg(data);
    setRecipients((data.recipients || []).join(", "));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", {
        email_enabled: cfg.email_enabled,
        min_severity: cfg.min_severity,
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setCfg(data);
      setRecipients((data.recipients || []).join(", "));
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post("/settings/test-email");
      toast.success(t("settings.testSent"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!cfg) return <Layout><div className="p-6 text-slate-400">{t("common.loading")}</div></Layout>;

  return (
    <Layout>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      <div className="p-6">
        <Card className="max-w-2xl border-slate-200 p-6 shadow-none">
          <div className="mb-5 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50">
              <Mail className="h-5 w-5 text-blue-600" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-900">{t("settings.emailAlerts")}</h3>
          </div>

          <div className={`mb-5 flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
            cfg.email_configured
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`} data-testid="smtp-status">
            {cfg.email_configured
              ? <><CheckCircle2 className="h-4 w-4" /> {t("settings.configured")}</>
              : <><AlertCircle className="h-4 w-4" /> {t("settings.notConfigured")}</>}
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-4">
              <div>
                <div className="text-sm font-medium text-slate-800">{t("settings.emailEnabled")}</div>
                <div className="text-xs text-slate-400">{t("settings.enableDesc")}</div>
              </div>
              <Switch checked={cfg.email_enabled} data-testid="email-enabled-switch"
                onCheckedChange={(v) => setCfg({ ...cfg, email_enabled: v })} />
            </div>

            <div>
              <Label>{t("settings.minSeverity")}</Label>
              <Select value={cfg.min_severity} onValueChange={(v) => setCfg({ ...cfg, min_severity: v })}>
                <SelectTrigger className="mt-1.5 w-64" data-testid="min-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">{t("settings.onlyCritical")}</SelectItem>
                  <SelectItem value="warning">{t("settings.warningAndCritical")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t("settings.recipients")}</Label>
              <Input data-testid="recipients-input" value={recipients}
                onChange={(e) => setRecipients(e.target.value)} className="mt-1.5"
                placeholder="alertas@dosatronic.es, soporte@dosatronic.es" />
              <p className="mt-1 text-xs text-slate-400">{t("settings.recipientsDesc")}</p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-settings">
                <Save className="mr-2 h-4 w-4" /> {t("common.save")}
              </Button>
              <Button variant="outline" onClick={sendTest} disabled={!cfg.email_configured} data-testid="send-test-email">
                <Send className="mr-2 h-4 w-4" /> {t("settings.sendTest")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
